import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useMovieData } from '../hooks/useMovieData';
import { useAvailableDates } from '../hooks/useAvailableDates'; // New import
import { useTooltip } from '../hooks/useTooltip';
import {
  getCurrentTimeInMinutes,
  getDateOptions,
  getMillisecondsUntilNextDay,
  getTodayDate,
  getTomorrowDate
} from '../utils/dateUtils';
import DesktopMovieSchedule from './DesktopMovieSchedule';
import MobileMovieSchedule from './MobileMovieSchedule';
import posthog from 'posthog-js';

const MovieSchedule = () => {
  const [selectedDate, setSelectedDate] = useState(getTodayDate);
  const [selectedCity, setSelectedCity] = useState("Amsterdam");
  const [selectedTimePeriod, setSelectedTimePeriod] = useState("all");
  const [layoutMode, setLayoutMode] = useState('grid');
  const [showSections, setShowSections] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [currentDate, setCurrentDate] = useState(getTodayDate);

  const topRatedScrollRef = useRef(null);
  const newReleasesScrollRef = useRef(null);
  const classicScrollRef = useRef(null);

  const { movies, error, isLoading } = useMovieData(selectedDate, selectedCity);
  const { availableDates, isLoading: datesLoading, error: datesError } = useAvailableDates(selectedCity, currentDate);
  const { tooltip, showTooltip, hideTooltip } = useTooltip();

  // Pass available dates to getDateOptions to filter out empty days
  const dateOptions = getDateOptions(availableDates);

  const timePeriodOptions = [
    { value: 'all', label: 'All Day' },
    { value: 'morning', label: 'Morning (6 - 12)' },
    { value: 'afternoon', label: 'Afternoon (12 - 18)' },
    { value: 'evening', label: 'Evening (18 - 24)' }
  ];

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    let timeoutId;

    const scheduleNextRollover = () => {
      timeoutId = window.setTimeout(() => {
        const today = getTodayDate();
        setCurrentDate(today);
        setSelectedDate(prevDate => prevDate < today ? today : prevDate);
        scheduleNextRollover();
      }, getMillisecondsUntilNextDay() + 1000);
    };

    scheduleNextRollover();

    return () => window.clearTimeout(timeoutId);
  }, []);

  // Update selected date if current date is no longer available
  useEffect(() => {
    if (availableDates.length > 0 && !availableDates.includes(selectedDate)) {
      // If the current selected date is not available, switch to the first available date
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate]);


  const handleDateChange = (optionValue) => {
    setSelectedDate(optionValue.value);
    posthog.capture('ChangeDate', {
      date: optionValue.value,
      city: selectedCity
    });
  };

  const handleCityChange = (optionValue) => {
    posthog.capture('ChangeCity', {
      city: optionValue.value,
      date: selectedDate
    });
    setSelectedCity(optionValue.value);
  };

  const handleTimePeriodChange = (optionValue) => {
    posthog.capture('ChangeTimeFilter', {
      time_period: optionValue.value,
      city: selectedCity,
      date: selectedDate
    });
    setSelectedTimePeriod(optionValue.value);
  };

  const handleMovieClick = (movie) => {
    posthog.capture('ClickMovie', {
      movie_title: movie.original_title,
      movie_id: movie.id,
      city: selectedCity,
      date: selectedDate
    });
  };

  const handleClickBooking = (movie, showtime, bookingUrl) => {
    posthog.capture('ClickBooking', {
      movie_title: movie.original_title,
      movie_id: movie.id,
      city: selectedCity,
      date: selectedDate,
      showtime: showtime.start_time,
      booking_url: bookingUrl
    });
  };

  const handleClickShare = (movie, showtime) => {
    posthog.capture('ClickShare', {
      movie_title: movie.original_title,
      movie_id: movie.id,
      city: selectedCity,
      date: selectedDate,
      showtime: showtime.start_time
    });
  };

  const toggleLayout = () => {
    setLayoutMode(prev => prev === 'grid' ? 'list' : 'grid');
  };

  const toggleSections = () => {
    setShowSections(prev => !prev);
  };

  const extractHourFromStartTime = useCallback((startTime) => {
    if (!startTime) return 0;
    const timeStr = startTime.toString().trim();
    if (timeStr.includes(':')) {
      const hour = parseInt(timeStr.split(':')[0]);
      return isNaN(hour) ? 0 : hour;
    }
    return 0;
  }, []);

  const extractMinuteFromStartTime = useCallback((startTime) => {
    if (!startTime) return 0;
    const timeStr = startTime.toString().trim();
    if (timeStr.includes(':')) {
      const parts = timeStr.split(':');
      if (parts.length >= 2) {
        const minute = parseInt(parts[1]);
        return isNaN(minute) ? 0 : minute;
      }
    }
    return 0;
  }, []);

  const filterMoviesByTimePeriod = useCallback((movies, timePeriod) => {
    const isSelectedDateToday = selectedDate === currentDate;

    if (timePeriod === 'all') {
      return movies
          .map(movie => {
            if (!movie.showtimes || !Array.isArray(movie.showtimes)) {
              return null;
            }

            const validShowtimes = movie.showtimes.filter(showtime => {
              if (!isSelectedDateToday) {
                return true;
              }

              const hour = extractHourFromStartTime(showtime.start_time);
              const minute = extractMinuteFromStartTime(showtime.start_time);
              const showtimeInMinutes = hour * 60 + minute;
              const currentTime = getCurrentTimeInMinutes();

              return showtimeInMinutes > currentTime;
            });

            if (validShowtimes.length === 0) {
              return null;
            }

            return {
              ...movie,
              showtimes: validShowtimes
            };
          })
          .filter(movie => movie !== null);
    }

    return movies
        .map(movie => {
          if (!movie.showtimes || !Array.isArray(movie.showtimes)) {
            return null;
          }

          const filteredShowtimes = movie.showtimes.filter(showtime => {
            const hour = extractHourFromStartTime(showtime.start_time);
            const minute = extractMinuteFromStartTime(showtime.start_time);

            if (isSelectedDateToday) {
              const showtimeInMinutes = hour * 60 + minute;
              const currentTime = getCurrentTimeInMinutes();

              if (showtimeInMinutes <= currentTime) {
                return false;
              }
            }

            switch (timePeriod) {
              case 'morning':
                return hour >= 6 && hour < 12;
              case 'afternoon':
                return hour >= 12 && hour < 18;
              case 'evening':
                return hour >= 18 && hour <= 23;
              default:
                return true;
            }
          });

          if (filteredShowtimes.length === 0) {
            return null;
          }

          return {
            ...movie,
            showtimes: filteredShowtimes
          };
        })
        .filter(movie => movie !== null);
  }, [currentDate, extractHourFromStartTime, extractMinuteFromStartTime, selectedDate]);

  // Calculate filtered movies using useMemo so it's available for useEffect
  const timeFilteredMovies = useMemo(() => {
    return filterMoviesByTimePeriod(movies, selectedTimePeriod);
  }, [filterMoviesByTimePeriod, movies, selectedTimePeriod]);

  // Auto-switch to tomorrow if no movies left for today
  useEffect(() => {
    const today = getTodayDate();
    const tomorrow = getTomorrowDate();
    
    // Only auto-switch if:
    // 1. Currently viewing today
    // 2. There are movies scheduled for today (in original movies list)
    // 3. But no movies left after time filtering (all have started)
    // 4. Tomorrow is available
    if (selectedDate === today && 
        movies.length > 0 && 
        timeFilteredMovies.length === 0 && 
        availableDates.includes(tomorrow)) {
      
      console.log('Auto-switching to tomorrow - no more movies left for today');
      setSelectedDate(tomorrow);
      
      // Track this auto-switch for analytics
      posthog.capture('AutoSwitchToTomorrow', {
        original_date: today,
        new_date: tomorrow,
        city: selectedCity,
        time: new Date().toLocaleTimeString('en-GB', {
          timeZone: 'Europe/Amsterdam',
          hour: '2-digit',
          minute: '2-digit'
        })
      });
    }
  }, [movies, timeFilteredMovies, selectedDate, availableDates, selectedCity]);

  const scrollLeft = () => {
    if (topRatedScrollRef.current) {
      const cardWidth = topRatedScrollRef.current.querySelector('.movie-card')?.offsetWidth || 300;
      const gap = 8;
      const scrollAmount = isMobile ? (cardWidth + gap) * 2 : (cardWidth + gap) * 4;
      topRatedScrollRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollRight = () => {
    if (topRatedScrollRef.current) {
      const cardWidth = topRatedScrollRef.current.querySelector('.movie-card')?.offsetWidth || 300;
      const gap = 8;
      const scrollAmount = isMobile ? (cardWidth + gap) * 2 : (cardWidth + gap) * 4;
      topRatedScrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollNewReleasesLeft = () => {
    if (newReleasesScrollRef.current) {
      const cardWidth = newReleasesScrollRef.current.querySelector('.movie-card')?.offsetWidth || 300;
      const gap = 8;
      const scrollAmount = isMobile ? (cardWidth + gap) * 2 : (cardWidth + gap) * 4;
      newReleasesScrollRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollNewReleasesRight = () => {
    if (newReleasesScrollRef.current) {
      const cardWidth = newReleasesScrollRef.current.querySelector('.movie-card')?.offsetWidth || 300;
      const gap = 8;
      const scrollAmount = isMobile ? (cardWidth + gap) * 2 : (cardWidth + gap) * 4;
      newReleasesScrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollClassicLeft = () => {
    if (classicScrollRef.current) {
      const cardWidth = classicScrollRef.current.querySelector('.movie-card')?.offsetWidth || 300;
      const gap = 8;
      const scrollAmount = isMobile ? (cardWidth + gap) * 2 : (cardWidth + gap) * 4;
      classicScrollRef.current.scrollBy({
        left: -scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  const scrollClassicRight = () => {
    if (classicScrollRef.current) {
      const cardWidth = classicScrollRef.current.querySelector('.movie-card')?.offsetWidth || 300;
      const gap = 8;
      const scrollAmount = isMobile ? (cardWidth + gap) * 2 : (cardWidth + gap) * 4;
      classicScrollRef.current.scrollBy({
        left: scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  if (error || datesError) {
    return <div className="error-container">Error: {error?.message || datesError?.message}</div>;
  }

  const getTopRatedMovies = (movies) => {
    return movies
        .filter(movie => {
          const imdbRating = parseFloat(movie.imdb_rating) || 0;
          const rottenRating = parseInt(movie.rotten_tomatoes_rating) || 0;
          return imdbRating >= 9.0 || rottenRating >= 90;
        })
        .sort((a, b) => {
          const aImdb = parseFloat(a.imdb_rating) || 0;
          const bImdb = parseFloat(b.imdb_rating) || 0;
          if (aImdb !== bImdb) return bImdb - aImdb;

          const aRotten = parseInt(a.rotten_tomatoes_rating) || 0;
          const bRotten = parseInt(b.rotten_tomatoes_rating) || 0;
          return bRotten - aRotten;
        });
  };

  const getNewReleaseMovies = (movies) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const cutoffDate = new Date(today);
    cutoffDate.setDate(cutoffDate.getDate() - 60);

    return movies
        .filter(movie => {
          if (!movie.release_date) return false;

          const releaseDate = new Date(`${movie.release_date}T00:00:00`);
          if (Number.isNaN(releaseDate.getTime())) return false;

          return releaseDate >= cutoffDate && releaseDate <= today;
        })
        .sort((a, b) => new Date(`${b.release_date}T00:00:00`) - new Date(`${a.release_date}T00:00:00`));
  };

  const getClassicMovies = (movies) => {
    return movies
        .filter(movie => {
          const year = parseInt(movie.year_of_release) || 0;
          return year > 0 && year < 1990;
        })
        .sort((a, b) => {
          const aYear = parseInt(a.year_of_release) || 0;
          const bYear = parseInt(b.year_of_release) || 0;
          return bYear - aYear; // Sort by year descending (newest classics first)
        });
  };

  const newReleaseMovies = getNewReleaseMovies(timeFilteredMovies);
  const topRatedMovies = getTopRatedMovies(timeFilteredMovies);
  const classicMovies = getClassicMovies(timeFilteredMovies);
  const remainingMovies = timeFilteredMovies.filter(movie =>
      !topRatedMovies.some(topRated => topRated.id === movie.id) &&
      !classicMovies.some(classic => classic.id === movie.id)
  );

  const commonProps = {
    selectedDate,
    selectedCity,
    selectedTimePeriod,
    layoutMode,
    showSections,
    futureMovies: timeFilteredMovies,
    newReleaseMovies,
    topRatedMovies,
    classicMovies,
    remainingMovies,
    dateOptions,
    timePeriodOptions,
    handleDateChange,
    handleCityChange,
    handleTimePeriodChange,
    toggleLayout,
    scrollLeft,
    scrollRight,
    topRatedScrollRef,
    scrollNewReleasesLeft,
    scrollNewReleasesRight,
    newReleasesScrollRef,
    scrollClassicLeft,
    scrollClassicRight,
    classicScrollRef,
    showTooltip,
    hideTooltip,
    tooltip,
    isLoading: isLoading || datesLoading // Combined loading state
  };

  return (
      <div className={`movie-schedule-container ${isMobile ? 'mobile-layout' : 'desktop-layout'}`}>
        {(isLoading || datesLoading) ? (
            <div className="loading-container">
              <p>Loading movies...</p>
            </div>
        ) : (
            isMobile ? (
                <MobileMovieSchedule
                    {...commonProps}
                    toggleSections={toggleSections}
                    onMovieClick={handleMovieClick}
                    onBookingClick={handleClickBooking}
                    onShareClick={handleClickShare}
                />
            ) : (
                <DesktopMovieSchedule
                    {...commonProps}
                    onMovieClick={handleMovieClick}
                    onBookingClick={handleClickBooking}
                    onShareClick={handleClickShare}
                />
            )
        )}
      </div>
  );
};

export default MovieSchedule;
